import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { FiDownload } from "react-icons/fi";
import { COLORS } from "../utils/constants";
import { formatCurrency, formatDate } from "../utils/helpers";
import { transactionsApi } from "../api";

const Container = styled.div`
  background: ${COLORS.white};
  border: 1px solid ${COLORS.gray200};
  border-radius: 12px;
  padding: 20px;
`;

const Header = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const Title = styled.h3`
  color: ${COLORS.gray800};
  font-size: 16px;
  font-weight: 600;
  margin: 0;
`;

const Controls = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
`;

const MonthInput = styled.input`
  border: 1px solid ${COLORS.gray300};
  border-radius: 8px;
  color: ${COLORS.gray700};
  font-size: 14px;
  padding: 6px 12px;

  &:focus {
    border-color: ${COLORS.primary};
    outline: none;
  }
`;

const DownloadButton = styled.button`
  align-items: center;
  background: ${COLORS.primary};
  border: none;
  border-radius: 8px;
  color: ${COLORS.white};
  cursor: pointer;
  display: flex;
  font-size: 13px;
  font-weight: 500;
  gap: 6px;
  padding: 7px 14px;

  &:hover {
    background: ${COLORS.primaryDark};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const Table = styled.table`
  border-collapse: collapse;
  font-size: 14px;
  width: 100%;
`;

const Th = styled.th`
  border-bottom: 2px solid ${COLORS.gray200};
  color: ${COLORS.gray500};
  font-size: 12px;
  font-weight: 600;
  padding: 8px 12px;
  text-align: left;
  text-transform: uppercase;
`;

const ThRight = styled(Th)`
  text-align: right;
`;

const Td = styled.td`
  border-bottom: 1px solid ${COLORS.gray100};
  color: ${COLORS.gray700};
  padding: 10px 12px;
`;

const TdRight = styled(Td)`
  color: ${COLORS.success};
  font-weight: 500;
  text-align: right;
`;

const TotalRow = styled.tr`
  font-weight: 600;

  td {
    border-bottom: none;
    border-top: 2px solid ${COLORS.gray200};
    color: ${COLORS.gray800};
    padding-top: 12px;
  }
`;

const EmptyState = styled.div`
  color: ${COLORS.gray400};
  font-size: 14px;
  padding: 24px 0;
  text-align: center;
`;

const MobileCard = styled.div`
  border-bottom: 1px solid ${COLORS.gray100};
  display: none;
  padding: 12px 0;

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 600px) {
    display: block;
  }
`;

const MobileRow = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const MobileDate = styled.span`
  color: ${COLORS.gray500};
  font-size: 12px;
`;

const MobileAmount = styled.span`
  color: ${COLORS.success};
  font-size: 15px;
  font-weight: 500;
`;

const MobileDesc = styled.div`
  color: ${COLORS.gray500};
  font-size: 13px;
  margin-top: 2px;
`;

const MobileList = styled.div`
  display: none;

  @media (max-width: 600px) {
    display: block;
  }
`;

const DesktopTable = styled.div`
  @media (max-width: 600px) {
    display: none;
  }
`;

const TotalSummary = styled.div`
  border-top: 2px solid ${COLORS.gray200};
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: flex-end;
  margin-top: 8px;
  padding-top: 12px;
`;

const TotalItem = styled.div`
  color: ${COLORS.gray800};
  font-size: 15px;
  font-weight: 600;
`;

const getMonthLabel = (month) => {
  const [y, m] = month.split("-");
  const date = new Date(Number(y), Number(m) - 1);
  return date.toLocaleDateString("en-US", { year: "numeric", month: "long" });
};

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const FundingSummary = () => {
  const [month, setMonth] = useState(getCurrentMonth);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await transactionsApi.fundingSummary(month);
      setData(result);
    } catch (err) {
      console.error("Failed to load funding summary:", err);
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const transactions = data ? data.transactions : [];
  const totals = data ? data.totals : {};
  const totalEntries = Object.entries(totals);

  const generatePdf = async () => {
    const { default: jsPDF } = await import("jspdf");
    const { default: autoTable } = await import("jspdf-autotable");
    const doc = new jsPDF();
    const monthLabel = getMonthLabel(month);

    // Title
    doc.setFontSize(18);
    doc.text("Petty Cash Fund Transfer Statement", 14, 20);

    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(`Period: ${monthLabel}`, 14, 28);
    doc.text(`Generated: ${new Date().toLocaleDateString("en-US")}`, 14, 34);

    if (transactions.length === 0) {
      doc.setFontSize(12);
      doc.setTextColor(150);
      doc.text("No fund transfers recorded for this period.", 14, 50);
    } else {
      const tableRows = transactions.map((t, i) => {
        return [
          String(i + 1),
          formatDate(t.date),
          t.currency,
          formatCurrency(t.amount, t.currency),
          t.description || "--",
        ];
      });

      autoTable(doc, {
        startY: 42,
        head: [["#", "Date", "Currency", "Amount", "Description"]],
        body: tableRows,
        styles: { fontSize: 10 },
        headStyles: {
          fillColor: [37, 99, 235],
          textColor: [255, 255, 255],
        },
        columnStyles: {
          0: { cellWidth: 12, halign: "center" },
          2: { cellWidth: 22, halign: "center" },
          3: { halign: "right" },
        },
      });

      // Totals section
      const finalY = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.text("Summary", 14, finalY);

      const totalRows = totalEntries.map(([currency, amount]) => [
        currency,
        formatCurrency(amount, currency),
      ]);

      autoTable(doc, {
        startY: finalY + 4,
        head: [["Currency", "Total Transferred"]],
        body: totalRows,
        styles: { fontSize: 11 },
        headStyles: {
          fillColor: [22, 163, 74],
          textColor: [255, 255, 255],
        },
        columnStyles: {
          1: { halign: "right", fontStyle: "bold" },
        },
        tableWidth: 90,
      });
    }

    doc.save(`petty-cash-funding-${month}.pdf`);
  };

  return (
    <Container>
      <Header>
        <Title>Monthly Funding Summary</Title>
        <Controls>
          <MonthInput
            type="month"
            value={month}
            onChange={(e) => {
              if (e.target.value) setMonth(e.target.value);
            }}
          />
          <DownloadButton
            onClick={generatePdf}
            disabled={loading || transactions.length === 0}
          >
            <FiDownload size={14} /> Download PDF
          </DownloadButton>
        </Controls>
      </Header>

      {loading ? (
        <EmptyState>Loading...</EmptyState>
      ) : transactions.length === 0 ? (
        <EmptyState>No fund transfers for {getMonthLabel(month)}</EmptyState>
      ) : (
        <React.Fragment>
          <DesktopTable>
            <Table>
              <thead>
                <tr>
                  <Th>#</Th>
                  <Th>Date</Th>
                  <Th>Currency</Th>
                  <ThRight>Amount</ThRight>
                  <Th>Description</Th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((t, i) => (
                  <tr key={t.id}>
                    <Td>{i + 1}</Td>
                    <Td>{formatDate(t.date)}</Td>
                    <Td>{t.currency}</Td>
                    <TdRight>{formatCurrency(t.amount, t.currency)}</TdRight>
                    <Td>{t.description || "--"}</Td>
                  </tr>
                ))}
                {totalEntries.map(([currency, amount]) => (
                  <TotalRow key={currency}>
                    <Td />
                    <Td />
                    <Td>{currency}</Td>
                    <TdRight style={{ color: COLORS.gray800 }}>
                      {formatCurrency(amount, currency)}
                    </TdRight>
                    <Td>Total</Td>
                  </TotalRow>
                ))}
              </tbody>
            </Table>
          </DesktopTable>

          <MobileList>
            {transactions.map((t) => (
              <MobileCard key={t.id}>
                <MobileRow>
                  <MobileDate>{formatDate(t.date)}</MobileDate>
                  <MobileAmount>
                    {formatCurrency(t.amount, t.currency)}
                  </MobileAmount>
                </MobileRow>
                {t.description && <MobileDesc>{t.description}</MobileDesc>}
              </MobileCard>
            ))}
            <TotalSummary>
              {totalEntries.map(([currency, amount]) => (
                <TotalItem key={currency}>
                  Total: {formatCurrency(amount, currency)}
                </TotalItem>
              ))}
            </TotalSummary>
          </MobileList>
        </React.Fragment>
      )}
    </Container>
  );
};

export default FundingSummary;
